// @flow
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { range, isEmpty } from 'lodash';
import moment from 'moment';

import { ICONS } from 'Components/utils/icon/icons';
import { FilterWhen } from 'Components/widgets/criteria/criteria-when/filter-when/FilterWhen.jsx';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger.jsx';
import ReactAppContext from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import {
  changeTripDuration,
  clearDepartureDate,
  clearTripDuration,
} from 'Store/faceting/facetingSearch.action';
import { type PricesByDays } from 'Store/calendar/calendar.helper';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { OPEN_CRITERIA_WHEN_EVENT } from 'Components/widgets/criteria/criteria-when/constants';

import { isSeller } from 'Utils/seller/sellerUtils';
import { COLORS } from 'Components/utils/color/colors';

type CriteriaWhenProps = {
  durationOptions: Array<number>,
  prices?: PricesByDays,
  defaultDuration?: number,
  isJoyview?: boolean,
};

const selector = (state: ReduxState) => ({
  departureDate: state.tripCriteria.departureDate,
  duration: state.tripCriteria.duration,
});

export const CriteriaWhen = (props: CriteriaWhenProps) => {
  const appContext = useContext(ReactAppContext);
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const { departureDate, duration } = useSelector(selector);
  const [inputDuration, setInputDuration] = useState(duration);
  const [inputDepartureDate, setInputDepartureDate] = useState(departureDate);
  const dispatch = useDispatch();
  const track = useTracking({ actionGA: criteriaContext.trackingActionName });

  useEffect(
    () => {
      if (departureDate !== inputDepartureDate) {
        setInputDepartureDate(departureDate);
      }
    },
    [departureDate],
  );

  useEffect(
    () => {
      if (duration !== inputDuration) {
        setInputDuration(duration);
      }
    },
    [duration],
  );

  useEffect(
    () => {
      setCriteriaContext({ criteriaWhenIsSet: Boolean(departureDate && duration) });
    },
    [departureDate, duration],
  );

  const openCriteriaEventHandler = () => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaWhen',
    });
  };

  useEffect(() => {
    document.addEventListener(OPEN_CRITERIA_WHEN_EVENT, openCriteriaEventHandler);
    return () => document.removeEventListener(OPEN_CRITERIA_WHEN_EVENT, openCriteriaEventHandler);
  }, []);

  const saveDateAndDuration = () => {
    if (!inputDepartureDate) {
      dispatch(clearDepartureDate());
      dispatch(clearTripDuration());
    } else if (departureDate) {
      const currentDuration = inputDuration || props.defaultDuration || DEFAULT_TRIP_DURATION;
      if (currentDuration !== duration) {
        dispatch(changeTripDuration(currentDuration));
      }
    }

    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaWhen',
    });
  };

  const labelPartsMobile = [];

  if (departureDate && duration) {
    labelPartsMobile.push(moment(departureDate).format('DD/MM'));
    const arrival = moment(departureDate).add({ days: duration });
    labelPartsMobile.push(arrival.format('DD/MM'));
  }

  const labelParts = [t('faceting.when.arrivalDateTitle'), t('faceting.when.departureDateTitle')];
  if (departureDate) {
    labelParts[0] = moment(departureDate).format('DD MMM');
  }
  if (duration && departureDate) {
    labelParts[1] = moment(departureDate)
      .add({ days: duration })
      .format('DD MMM');
  }

  const durationOptions = isSeller(appContext) ? range(1, 31) : props.durationOptions;

  const hasPrices = !isEmpty(props.prices);

  const buttonLabel = (
    <React.Fragment>
      <span
        className="hidden lg:flex flex-row py-0 flex-1 overflow-hidden items-center whitespace-no-wrap button-default"
        style={{ height: '1px' }}
      >
        {labelParts.join(' - ')}
      </span>
      <span className="lg:hidden overflow-hidden button-default" style={{ height: '1px' }}>
        {labelPartsMobile.length > 1 && (
          <div className="rtl:ml-2 flex flex-col justify-around button-default py-1">
            {labelPartsMobile.map((labelSplited, key) => (
              <span key={`${labelSplited}_${key}`}>{labelSplited}</span>
            ))}
          </div>
        )}
      </span>
    </React.Fragment>
  );

  const trackDepartureDateChange = (value: moment$Moment) => {
    if (criteriaContext.trackingActionName) {
      track({
        labelGA: `when_${value.format('DD/MM/YYYY')}`,
      });
    }
  };

  const trackDurationChange = (value: number) => {
    if (criteriaContext.trackingActionName) {
      const labelGA = `duration_${value}`;
      track({
        labelGA: labelGA,
      });
    }
  };

  return (
    <CriteriaTrigger
      buttonLabel={buttonLabel}
      buttonIcon={ICONS.DATE}
      buttonIsFilled={criteriaContext.criteriaWhenIsSet}
      isOpen={criteriaDisplayContext.criteriaWhenIsOpen}
      isJoyview={props.isJoyview}
      trackingLabel="when"
      onOpen={() => {
        dispatchCriteriaDisplayContext({
          type: ACTIONS.OPEN_CRITERIA,
          payload: 'criteriaWhen',
        });
      }}
      onClose={saveDateAndDuration}
      onClear={() => {
        dispatch(clearTripDuration());
        dispatch(clearDepartureDate());
      }}
      buttons={[
        {
          children: t('faceting.reset'),
          theme: 'link',
          onClick: () => {
            setInputDuration();
            setInputDepartureDate();
          },
          className: 'text-sm cursor-pointer',
          dataTestId: 'criteria-when-reset',
        },
        {
          children: t('faceting.validate'),
          color: COLORS.WHITE,
          colorBg: COLORS.TURQUOISE,
          dataTestId: 'criteria-when-validate',
          onClick: () => {
            saveDateAndDuration();
            dispatchCriteriaDisplayContext({
              type: ACTIONS.OPEN_NEXT_CRITERIA,
              payload: { currentCriteria: 'criteriaWhen', criteriaContext },
            });
          },
        },
      ]}
      reassurance={{ text: t('faceting.when.reinsurance'), shouldDisplay: hasPrices }}
    >
      <FilterWhen
        defaultDuration={props.defaultDuration}
        optionsDuration={durationOptions}
        prices={props.prices}
        changeTripDuration={time => {
          if (time) {
            setInputDuration(time);
            trackDurationChange(time);
          }
        }}
        changeDepartureDate={(value: ?Date) => {
          if (!value) {
            setInputDepartureDate();
            setInputDuration();
            return;
          }

          const newDepartureDate = moment(value);
          const timestamp = newDepartureDate.valueOf();

          if (inputDepartureDate !== timestamp) {
            dispatch(setDepartureDate(timestamp));
            dispatch(clearTripDuration());

            setInputDepartureDate(timestamp);
            setInputDuration();

            setCriteriaContext({ defaultValueForCalendar: newDepartureDate });
            trackDepartureDateChange(newDepartureDate);
          }
        }}
        duration={inputDuration}
        departureDate={inputDepartureDate}
        openCalendarToDate={criteriaContext.defaultValueForCalendar}
        dateFormat={t('booking.form.dateFormat')}
      />
    </CriteriaTrigger>
  );
};
